/**
 * PostImportTour Component
 *
 * A 4-step guided tour shown to first-time users after importing repositories.
 * Displayed as a fixed bottom-right card with step navigation.
 * Dismissal state persisted to localStorage.
 *
 * @module components/onboarding/PostImportTour
 */

import React, { useState, useEffect, useCallback } from 'react';
import PropTypes from 'prop-types';
import {
  X,
  ChevronLeft,
  ChevronRight,
  CheckCircle2,
  BarChart3,
  Lightbulb,
  Share2,
  FolderOpen,
} from 'lucide-react';

const STORAGE_KEY = 'portfolistic_post_import_tour_done';

const STEPS = [
  {
    icon: FolderOpen,
    title: 'Repos imported!',
    description:
      'Your repositories have been imported. Click any repo card below to explore its details.',
    iconColor: 'text-success-400',
    bgColor: 'bg-success-500/10',
  },
  {
    icon: BarChart3,
    title: 'Run Analysis',
    description:
      'Navigate to the Overview tab and click "Run Analysis" to let AI analyze your code quality, skills, and patterns.',
    iconColor: 'text-primary-400',
    bgColor: 'bg-primary-500/10',
  },
  {
    icon: Lightbulb,
    title: 'Review Insights',
    description:
      'After analysis, visit the Insights tab to see AI-powered recommendations tailored to your repositories.',
    iconColor: 'text-warning-400',
    bgColor: 'bg-warning-500/10',
  },
  {
    icon: Share2,
    title: 'Share Your Portfolio',
    description:
      'Export or share your portfolio with recruiters from the Settings tab. Your work, beautifully presented.',
    iconColor: 'text-secondary-400',
    bgColor: 'bg-secondary-500/10',
  },
];

/**
 * Returns true if the tour has been permanently dismissed.
 */
export const isPostImportTourDone = () => {
  try {
    return localStorage.getItem(STORAGE_KEY) === 'true';
  } catch {
    return false;
  }
};

/**
 * Mark the tour as permanently done.
 */
export const markPostImportTourDone = () => {
  try {
    localStorage.setItem(STORAGE_KEY, 'true');
  } catch {
    // localStorage unavailable — silently ignore
  }
};

/**
 * Reset the tour so it shows again (used from Help menu).
 */
export const resetPostImportTour = () => {
  try {
    localStorage.removeItem(STORAGE_KEY);
  } catch {
    // silently ignore
  }
};

const PostImportTour = ({ repoCount = 0, onClose }) => {
  const [step, setStep] = useState(0);
  const [visible, setVisible] = useState(false);

  // Only show if not previously dismissed
  useEffect(() => {
    if (!isPostImportTourDone()) {
      setVisible(true);
    }
  }, []);

  const dismiss = useCallback(
    (permanent = true) => {
      if (permanent) {
        markPostImportTourDone();
      }
      setVisible(false);
      onClose?.();
    },
    [onClose]
  );

  const handleFinish = useCallback(() => dismiss(true), [dismiss]);
  const handleSkip = useCallback(() => dismiss(true), [dismiss]);

  if (!visible) return null;

  const current = STEPS[step];
  const Icon = current.icon;
  const isFirst = step === 0;
  const isLast = step === STEPS.length - 1;

  const stepLabel = `Step ${step + 1} of ${STEPS.length}`;
  const firstStepTitle =
    step === 0 && repoCount > 0
      ? `${repoCount} repo${repoCount !== 1 ? 's' : ''} imported!`
      : current.title;

  return (
    <div
      className="fixed bottom-6 right-6 z-[1300] w-80 glass rounded-xl shadow-glass-lg border border-white/10 p-5"
      role="dialog"
      aria-modal="false"
      aria-label="Getting started tour"
      aria-live="polite"
    >
      {/* Header */}
      <div className="flex items-start justify-between mb-4">
        <div className={`p-2 rounded-lg ${current.bgColor} flex-shrink-0`}>
          <Icon className={`w-5 h-5 ${current.iconColor}`} aria-hidden="true" />
        </div>
        <button
          onClick={handleSkip}
          className="p-1 rounded text-gray-500 hover:text-white hover:bg-white/10 transition-colors flex-shrink-0"
          aria-label="Skip tour"
        >
          <X className="w-4 h-4" />
        </button>
      </div>

      {/* Content */}
      <h3 className="text-white font-semibold text-sm mb-1">
        {firstStepTitle}
      </h3>
      <p className="text-gray-300 text-xs leading-relaxed mb-4">
        {current.description}
      </p>

      {/* Step dots */}
      <div className="flex items-center justify-center gap-1.5 mb-4" aria-label={stepLabel}>
        {STEPS.map((_, i) => (
          <div
            key={i}
            className={`h-1.5 rounded-full transition-all duration-200 ${
              i === step
                ? 'w-4 bg-primary-400'
                : i < step
                ? 'w-1.5 bg-primary-600'
                : 'w-1.5 bg-white/20'
            }`}
            aria-hidden="true"
          />
        ))}
      </div>

      {/* Navigation */}
      <div className="flex items-center justify-between">
        <button
          onClick={() => setStep(s => Math.max(0, s - 1))}
          disabled={isFirst}
          className="flex items-center gap-1 px-3 py-1.5 text-xs text-gray-400 hover:text-white
                     hover:bg-white/10 rounded-lg transition-colors disabled:opacity-30 disabled:cursor-not-allowed"
          aria-label="Previous step"
        >
          <ChevronLeft className="w-3.5 h-3.5" />
          Back
        </button>

        <span className="text-xs text-gray-500">{stepLabel}</span>

        {isLast ? (
          <button
            onClick={handleFinish}
            className="flex items-center gap-1 px-3 py-1.5 text-xs bg-primary-500/20 hover:bg-primary-500/30
                       text-primary-300 rounded-lg transition-colors"
          >
            <CheckCircle2 className="w-3.5 h-3.5" />
            Done
          </button>
        ) : (
          <button
            onClick={() => setStep(s => Math.min(STEPS.length - 1, s + 1))}
            className="flex items-center gap-1 px-3 py-1.5 text-xs bg-primary-500/20 hover:bg-primary-500/30
                       text-primary-300 rounded-lg transition-colors"
            aria-label="Next step"
          >
            Next
            <ChevronRight className="w-3.5 h-3.5" />
          </button>
        )}
      </div>
    </div>
  );
};

PostImportTour.propTypes = {
  /** Number of repos imported (used in step 1 title) */
  repoCount: PropTypes.number,
  /** Called when tour is dismissed */
  onClose: PropTypes.func,
};

export default PostImportTour;
